/**
 * useOAuthCompletion Hook
 *
 * Handles OAuth session completion and redirect logic for external connectors.
 * Prevents race conditions and manages the complex flow of linking users to sessions.
 *
 * Flow:
 * 1. Link current authenticated user to OAuth session
 * 2. Complete the OAuth session
 * 3. Redirect back to the connector (ChatGPT, Claude, Electron, etc.)
 */

import { useCallback, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { trackEvent } from "../utils/browserAnalytics";
import { identifyPostHogUser } from "../utils/posthogConfig";
import { oauthClientHostname } from "../utils/productTelemetry";

export interface OAuthCompletionResult {
    handleComplete: (redirect?: boolean) => Promise<void>;
    isRedirecting: boolean;
    redirectUri: string | null;
    retry: () => void;
}

export function useOAuthCompletion(oauthSessionId: string | null): OAuthCompletionResult {
    const { user } = useAuth();
    // Prevent multiple simultaneous calls (race condition guard)
    const redirectInitiated = useRef(false);
    // State to trigger re-renders for UI updates
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [redirectUri, setRedirectUri] = useState<string | null>(null);

    const handleComplete = useCallback(
        async (redirect = true) => {
            if (!oauthSessionId) {
                console.error("❌ [AUTH] Missing OAuth Session ID");
                return;
            }

            // Prevent multiple simultaneous calls
            if (redirectInitiated.current) {
                console.log("🛑 [AUTH] Redirect already initiated, skipping duplicate call");
                return;
            }
            redirectInitiated.current = true;
            setIsRedirecting(true);

            try {
                // Complete OAuth flow - backend auto-links user and generates auth code
                console.log("🎯 [AUTH] Completing OAuth session:", oauthSessionId);

                const response = await fetch(`/auth/session/${oauthSessionId}/complete`, {
                    method: "POST",
                    credentials: "include", // Send session cookie for auto-linking
                });

                if (!response.ok) {
                    // Read the error body once (a Response body can only be consumed once).
                    const errorData = await response.json().catch(() => ({}));

                    // Being signed in is not approval to connect an unrecognised app. The
                    // server withholds the code until the user approves it on the approval
                    // screen; send them there instead of surfacing an error.
                    if (response.status === 403 && errorData?.error === "consent_required") {
                        const authOrigin = import.meta.env.VITE_AUTH_URL || window.location.origin;
                        window.location.href = `${authOrigin}/approve?session_id=${encodeURIComponent(oauthSessionId)}`;
                        return;
                    }

                    throw new Error(errorData.error_description || "Failed to complete authentication");
                }

                const data = await response.json();
                console.log("✅ [AUTH] OAuth session completed, redirecting:", data.redirect_uri);
                setRedirectUri(data.redirect_uri);

                // Track successful completion
                // We can try to get the user from context if available, or just rely on cookie session for future events
                // Ideally we should have the user ID here.
                // Since we are likely authenticated (required for this endpoint), useAuth might have it.
                // But useAuth might not have loaded yet if this is a fresh page load.
                // However, the backend endpoint required auth, so we are authenticated.

                if (user) {
                    identifyPostHogUser(user.id, {
                        email: user.email,
                    });
                }

                trackEvent("oauth_redirect_completed", {
                    provider: "google", // Currently only google supported
                    redirect_uri: data.redirect_uri,
                    client_name: oauthClientHostname(data.redirect_uri),
                });

                if (redirect) {
                    window.location.href = data.redirect_uri;
                }
            } catch (error) {
                console.error("❌ [AUTH] Failed to complete OAuth session:", error);
                alert("Failed to complete authentication: " + (error instanceof Error ? error.message : String(error)));
                // Redirect to root to avoid infinite loop
                window.location.href = "/";
            }
        },
        [
            oauthSessionId,
            user,
        ],
    );

    const retry = useCallback(() => {
        if (redirectUri) {
            console.log("🔁 [AUTH] Retrying deep-link redirect:", redirectUri);
            window.location.href = redirectUri;
        } else {
            redirectInitiated.current = false;
            handleComplete();
        }
    }, [
        redirectUri,
        handleComplete,
    ]);

    return {
        handleComplete,
        isRedirecting,
        redirectUri,
        retry,
    };
}
